import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@src/lib/utils/cn";
import XIcon from "@src/lib/icons/12px/x";
import ScrollableFlexGrow from "@src/lib/ui/ScrollableFlexGrow";
import { textV4Variants } from "@src/lib/ui/Text";
import {
  DIALOG_DESCRIPTION_CLASSNAME,
  DIALOG_OVERLAY_CLASSNAME,
  DIALOG_TITLE_CLASSNAME,
  OverlayOpenContext,
  getZIndexClass,
  useControllableOpen,
  useFrozenWhileClosed,
} from "@src/lib/ui/dialogShell";

/**
 * A side panel on Radix Dialog. It shares the Dialog shell: the scrim, the
 * elevated surface, the --modal-* motion tokens (a slide instead of a scale),
 * the close button and the frozen-while-closing content.
 */
const Sheet = ({
  open: openProp,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentPropsWithoutRef<
  typeof SheetPrimitive.Root
>): React.JSX.Element => {
  const [open, setOpen] = useControllableOpen({
    open: openProp,
    defaultOpen,
    onOpenChange,
  });
  return (
    <OverlayOpenContext.Provider value={open}>
      <SheetPrimitive.Root open={open} onOpenChange={setOpen} {...props} />
    </OverlayOpenContext.Provider>
  );
};
Sheet.displayName = "Sheet";

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    // The scrim fades out over the panel's slide-out, so it doesn't vanish
    // halfway through.
    className={cn(
      DIALOG_OVERLAY_CLASSNAME,
      "data-[state=closed]:duration-[var(--sheet-close-dur)]",
      className,
    )}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

// The panel slides on the drawer ease (no fade); the scrim keeps the modal
// fade. Reduced motion turns the slide off.
const SHEET_MOTION_CLASSNAME =
  "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:duration-[var(--sheet-open-dur)] data-[state=closed]:duration-[var(--sheet-close-dur)] data-[state=open]:ease-[var(--sheet-ease)] data-[state=closed]:ease-[var(--sheet-ease)] motion-reduce:data-[state=open]:animate-none motion-reduce:data-[state=closed]:animate-none";

const sheetVariants = cva(
  `fixed z-50 border-border-primary bg-bg-elevated text-text-primary will-change-transform outline-none ${SHEET_MOTION_CLASSNAME}`,
  {
    variants: {
      side: {
        // Top and bottom sheets float: rounded inner corners, a shadow, and
        // at most 85% of the viewport. Side sheets sit flush with only a
        // hairline on their inner edge.
        top: "inset-x-0 top-(--impersonation-banner-height) h-1/3 max-h-[85vh] overflow-hidden rounded-b-3xl border-b shadow-extra-large data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
        bottom:
          "inset-x-0 bottom-0 h-1/3 max-h-[85vh] overflow-hidden rounded-t-3xl border-t shadow-extra-large data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
        left: "top-(--impersonation-banner-height) bottom-0 left-0 w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm",
        right:
          "top-(--impersonation-banner-height) right-0 bottom-0 w-1/2 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
      },
    },
    defaultVariants: {
      side: "right",
    },
  },
);

type SheetLayout = {
  /** Whether the content renders its corner close button. */
  hasCloseButton: boolean;
  /** Width of the SheetActions group, so the header can clear it. */
  actionsWidth: number;
  reportActionsWidth: (width: number) => void;
};

const SheetLayoutContext = React.createContext<SheetLayout>({
  hasCloseButton: false,
  actionsWidth: 0,
  reportActionsWidth: () => undefined,
});

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  innerStyle?: React.CSSProperties;
  /** Hide the corner X, e.g. when the footer has a Cancel button. */
  hideCloseButton?: boolean;
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(
  (
    {
      side = "right",
      className,
      children,
      innerStyle = { padding: 24 },
      hideCloseButton = false,
      ...props
    },
    ref,
  ) => {
    const contentClassName = cn(sheetVariants({ side }), className);
    // Keep the last open render on screen while the slide-out runs.
    const frozenChildren = useFrozenWhileClosed(children);
    const renderedChildren = props.forceMount ? children : frozenChildren;
    const [actionsWidth, reportActionsWidth] = React.useState(0);
    const layout = React.useMemo(
      () => ({
        hasCloseButton: !hideCloseButton,
        actionsWidth,
        reportActionsWidth,
      }),
      [hideCloseButton, actionsWidth],
    );
    return (
      <SheetPortal>
        <SheetOverlay className={getZIndexClass(contentClassName)} />
        <SheetPrimitive.Content
          ref={ref}
          className={contentClassName}
          {...props}
        >
          <SheetLayoutContext.Provider value={layout}>
            <ScrollableFlexGrow
              className="h-full w-full"
              innerStyle={innerStyle}
            >
              {renderedChildren}
            </ScrollableFlexGrow>
          </SheetLayoutContext.Provider>
          {!hideCloseButton && (
            // Dialog's close button, centered on the display title's first
            // line: 24px padding + 24px line / 2 - 12px = top-6.
            <SheetPrimitive.Close className="absolute end-6 top-6 flex size-6 items-center justify-center rounded-md text-icon-secondary transition-colors before:absolute before:-inset-2 before:rounded-md hover:bg-state-hover focus-visible:ring-[3px] focus-visible:ring-brand-border focus-visible:outline-none disabled:pointer-events-none">
              <XIcon className="size-4 text-icon-secondary" />
              <span className="sr-only">Close</span>
            </SheetPrimitive.Close>
          )}
        </SheetPrimitive.Content>
      </SheetPortal>
    );
  },
);
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element => {
  const { hasCloseButton, actionsWidth } = React.useContext(SheetLayoutContext);
  // Clear the actions too: they sit at end-14 (end-6 without the X), so the
  // header pads their width plus that inset, minus the body's default 24px
  // padding (a custom innerStyle padding isn't accounted for), plus 8px.
  const actionsPadding = actionsWidth
    ? actionsWidth + (hasCloseButton ? 56 : 24) - 24 + 8
    : 0;
  return (
    <div
      className={cn(
        "flex flex-col gap-1 text-start",
        hasCloseButton && "pe-8",
        className,
      )}
      {...props}
      style={
        actionsPadding
          ? { paddingInlineEnd: actionsPadding, ...props.style }
          : props.style
      }
    />
  );
};
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>): React.JSX.Element => (
  <div
    className={cn(
      "mt-auto flex flex-col-reverse gap-2 pt-4 sm:flex-row sm:justify-end",
      className,
    )}
    {...props}
  />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn(
      textV4Variants({ variant: "display" }),
      DIALOG_TITLE_CLASSNAME,
      className,
    )}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn(
      textV4Variants({ variant: "compact-description" }),
      DIALOG_DESCRIPTION_CLASSNAME,
      className,
    )}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

type SheetActionsProps = {
  children: React.ReactNode;
  className?: string;
};

/** Header actions in the top corner, left of the close button. They sit in
 * the scroll body, so they scroll away with the header. */
const SheetActions: React.FC<SheetActionsProps> = (props) => {
  const { hasCloseButton, reportActionsWidth } =
    React.useContext(SheetLayoutContext);
  const ref = React.useRef<HTMLDivElement>(null);
  React.useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const report = () => reportActionsWidth(node.offsetWidth);
    report();
    if (typeof ResizeObserver === "undefined") {
      return () => reportActionsWidth(0);
    }
    const observer = new ResizeObserver(report);
    observer.observe(node);
    return () => {
      observer.disconnect();
      reportActionsWidth(0);
    };
  }, [reportActionsWidth]);
  return (
    <div
      ref={ref}
      className={cn(
        // top-5: a 32px md button centers on the X (24 + 24 / 2 = 36px).
        "absolute top-5 flex gap-2.5",
        hasCloseButton ? "end-14" : "end-6",
        props.className,
      )}
    >
      {props.children}
    </div>
  );
};

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
  SheetActions,
};
